import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PlusCircle, CheckCircle, XCircle, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = (token) => ({ Authorization: `Bearer ${token}` });

const STATUS_COLORS = {
  REQUESTED: "bg-yellow-100 text-yellow-800",
  UNDER_REVIEW: "bg-blue-100 text-blue-800",
  APPROVED: "bg-green-100 text-green-800",
  APPLIED: "bg-emerald-100 text-emerald-800",
  CANCELLED: "bg-gray-100 text-gray-700",
  REVERSED: "bg-red-100 text-red-800",
  REJECTED: "bg-red-100 text-red-800",
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Award Create Modal
// ─────────────────────────────────────────────────────────────────────────────
function CreateAwardModal({ open, onClose, token, schemes, onCreated }) {
  const [form, setForm] = useState({ scheme_id: "", student_account_id: "", amount: "", academic_period: "", notes: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.scheme_id || !form.student_account_id || !form.amount) {
      toast.error("Scheme, student account, and amount are required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/finance/fm4a/awards`, { ...form, coverage_basis: "FIXED_AMOUNT" }, { headers: hdrs(token) });
      toast.success("Scholarship award created");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create award");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="create-award-modal">
        <DialogHeader><DialogTitle>Create Scholarship Award</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Scholarship Scheme *</Label>
            <Select value={form.scheme_id} onValueChange={(v) => setForm(f => ({ ...f, scheme_id: v }))}>
              <SelectTrigger data-testid="award-scheme-select"><SelectValue placeholder="Select scheme" /></SelectTrigger>
              <SelectContent>
                {schemes.filter(s => s.active).map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name} ({s.scheme_code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Student Account ID *</Label>
            <Input data-testid="award-student-account-input" value={form.student_account_id} onChange={e => setForm(f => ({ ...f, student_account_id: e.target.value }))} placeholder="UUID of student account" />
          </div>
          <div>
            <Label>Amount *</Label>
            <Input data-testid="award-amount-input" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="e.g. 5000.00" />
          </div>
          <div>
            <Label>Academic Period</Label>
            <Input value={form.academic_period} onChange={e => setForm(f => ({ ...f, academic_period: e.target.value }))} placeholder="e.g. 2025-26 Sem 1" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="create-award-submit" onClick={submit} disabled={loading}>{loading ? "Creating…" : "Create Award"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Apply Award Modal
// ─────────────────────────────────────────────────────────────────────────────
function ApplyAwardModal({ open, onClose, token, award, onDone }) {
  const [chargeIds, setChargeIds] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const ids = chargeIds.split(",").map(s => s.trim()).filter(Boolean);
    if (!ids.length) { toast.error("At least one charge ID required"); return; }
    setLoading(true);
    try {
      await axios.patch(`${API}/api/finance/fm4a/awards/${award.id}/apply`, { charge_ids: ids }, { headers: hdrs(token) });
      toast.success("Scholarship applied — credit memos posted");
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="apply-award-modal">
        <DialogHeader><DialogTitle>Apply Scholarship Award — {award?.award_ref}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">Amount: <strong>{award?.amount}</strong></p>
          <div>
            <Label>Charge IDs (comma-separated) *</Label>
            <Textarea data-testid="apply-charge-ids-input" value={chargeIds} onChange={e => setChargeIds(e.target.value)} rows={2} placeholder="uuid1, uuid2" />
            <p className="text-xs text-muted-foreground mt-1">Credit memos will be posted against these charges (INV-FM4-07: charges are not mutated)</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="apply-award-submit" onClick={submit} disabled={loading}>{loading ? "Applying…" : "Apply Award"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reverse Modal (used for both awards and waivers)
// ─────────────────────────────────────────────────────────────────────────────
function ReverseModal({ open, onClose, token, entity, entityType, onDone }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Reversal reason is required (INV-FM4-14)"); return; }
    setLoading(true);
    const endpoint = entityType === "award"
      ? `${API}/api/finance/fm4a/awards/${entity.id}/reverse`
      : `${API}/api/finance/fm4a/waiver-requests/${entity.id}/reverse`;
    try {
      await axios.patch(endpoint, { reason }, { headers: hdrs(token) });
      toast.success("Reversal recorded — student liability reinstated");
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reverse"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="reverse-modal">
        <DialogHeader><DialogTitle>Reverse — {entity?.award_ref || entity?.request_ref}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-amber-700 bg-amber-50 rounded p-2">Reversal will reinstate student liability. Reason is mandatory per INV-FM4-14.</p>
          <div>
            <Label>Reason *</Label>
            <Textarea data-testid="reverse-reason-input" value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="Provide a clear reason for reversal…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="reverse-submit" variant="destructive" onClick={submit} disabled={loading}>{loading ? "Reversing…" : "Confirm Reversal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create Waiver Request Modal
// ─────────────────────────────────────────────────────────────────────────────
function CreateWaiverModal({ open, onClose, token, waiverTypes, onCreated }) {
  const [form, setForm] = useState({ waiver_type_id: "", student_account_id: "", charge_ids_str: "", requested_amount: "", notes: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.waiver_type_id || !form.student_account_id || !form.requested_amount) {
      toast.error("Type, student account, and amount are required"); return;
    }
    const charge_ids = form.charge_ids_str.split(",").map(s => s.trim()).filter(Boolean);
    setLoading(true);
    try {
      await axios.post(`${API}/api/finance/fm4a/waiver-requests`, {
        waiver_type_id: form.waiver_type_id,
        student_account_id: form.student_account_id,
        charge_ids,
        requested_amount: form.requested_amount,
        notes: form.notes,
      }, { headers: hdrs(token) });
      toast.success("Waiver request created");
      onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create waiver"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="create-waiver-modal">
        <DialogHeader><DialogTitle>New Waiver Request</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Waiver Type *</Label>
            <Select value={form.waiver_type_id} onValueChange={v => setForm(f => ({ ...f, waiver_type_id: v }))}>
              <SelectTrigger data-testid="waiver-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {waiverTypes.filter(wt => wt.active).map(wt => (
                  <SelectItem key={wt.id} value={wt.id}>{wt.name} ({wt.type_code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Student Account ID *</Label>
            <Input data-testid="waiver-student-account-input" value={form.student_account_id} onChange={e => setForm(f => ({ ...f, student_account_id: e.target.value }))} placeholder="UUID" />
          </div>
          <div>
            <Label>Charge IDs (comma-separated) *</Label>
            <Input data-testid="waiver-charge-ids-input" value={form.charge_ids_str} onChange={e => setForm(f => ({ ...f, charge_ids_str: e.target.value }))} placeholder="uuid1, uuid2" />
          </div>
          <div>
            <Label>Requested Amount *</Label>
            <Input data-testid="waiver-amount-input" type="number" value={form.requested_amount} onChange={e => setForm(f => ({ ...f, requested_amount: e.target.value }))} />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="create-waiver-submit" onClick={submit} disabled={loading}>{loading ? "Creating…" : "Submit Request"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Awards Tab
// ─────────────────────────────────────────────────────────────────────────────
function AwardsTab({ token, schemes }) {
  const [awards, setAwards] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [applyTarget, setApplyTarget] = useState(null);
  const [reverseTarget, setReverseTarget] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/finance/fm4a/awards`, { headers: hdrs(token) });
      setAwards(r.data);
    } catch { toast.error("Failed to load awards"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await axios.patch(`${API}/api/finance/fm4a/awards/${id}/approve`, { reason: "Approved via staff interface" }, { headers: hdrs(token) });
      toast.success("Award approved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cancel = async (id) => {
    try {
      await axios.patch(`${API}/api/finance/fm4a/awards/${id}/cancel`, { reason: "Cancelled via staff interface" }, { headers: hdrs(token) });
      toast.success("Award cancelled"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="awards-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold">Scholarship Awards</h3>
        <Button size="sm" data-testid="create-award-btn" onClick={() => setShowCreate(true)}>
          <PlusCircle className="w-4 h-4 mr-1" /> New Award
        </Button>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="awards-table">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Ref</th>
                <th className="py-2 pr-3">Scheme</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {awards.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-muted-foreground text-xs">No awards yet</td></tr>
              )}
              {awards.map(a => (
                <tr key={a.id} className="border-b hover:bg-muted/30" data-testid={`award-row-${a.id}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{a.award_ref}</td>
                  <td className="py-2 pr-3">{a.scheme_name}</td>
                  <td className="py-2 pr-3">{a.amount}</td>
                  <td className="py-2 pr-3"><StatusBadge status={a.status} /></td>
                  <td className="py-2 flex gap-1 flex-wrap">
                    {["REQUESTED","UNDER_REVIEW"].includes(a.status) && (
                      <Button size="xs" variant="outline" data-testid={`approve-award-${a.id}`} onClick={() => approve(a.id)}>
                        <CheckCircle className="w-3 h-3 mr-1" />Approve
                      </Button>
                    )}
                    {a.status === "APPROVED" && (
                      <Button size="xs" variant="outline" data-testid={`apply-award-${a.id}`} onClick={() => setApplyTarget(a)}>
                        Apply
                      </Button>
                    )}
                    {["REQUESTED","UNDER_REVIEW","APPROVED"].includes(a.status) && (
                      <Button size="xs" variant="outline" data-testid={`cancel-award-${a.id}`} onClick={() => cancel(a.id)}>
                        <XCircle className="w-3 h-3 mr-1" />Cancel
                      </Button>
                    )}
                    {a.status === "APPLIED" && (
                      <Button size="xs" variant="outline" data-testid={`reverse-award-${a.id}`} onClick={() => setReverseTarget(a)}>
                        <RotateCcw className="w-3 h-3 mr-1" />Reverse
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CreateAwardModal open={showCreate} onClose={() => setShowCreate(false)} token={token} schemes={schemes} onCreated={load} />
      {applyTarget && <ApplyAwardModal open={!!applyTarget} onClose={() => setApplyTarget(null)} token={token} award={applyTarget} onDone={load} />}
      {reverseTarget && <ReverseModal open={!!reverseTarget} onClose={() => setReverseTarget(null)} token={token} entity={reverseTarget} entityType="award" onDone={load} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Waiver Requests Tab
// ─────────────────────────────────────────────────────────────────────────────
function WaiverRequestsTab({ token, waiverTypes }) {
  const [requests, setRequests] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/finance/fm4a/waiver-requests`, { headers: hdrs(token) });
      setRequests(r.data);
    } catch { toast.error("Failed to load waiver requests"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await axios.patch(`${API}/api/finance/fm4a/waiver-requests/${id}/approve`, { reason: "Approved via staff interface" }, { headers: hdrs(token) });
      toast.success("Waiver approved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Rejection reason (required):");
    if (!reason) return;
    try {
      await axios.patch(`${API}/api/finance/fm4a/waiver-requests/${id}/reject`, { reason }, { headers: hdrs(token) });
      toast.success("Waiver rejected"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const apply = async (id) => {
    try {
      await axios.patch(`${API}/api/finance/fm4a/waiver-requests/${id}/apply`, {}, { headers: hdrs(token) });
      toast.success("Waiver applied — credit memos posted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="waiver-requests-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold">Waiver Requests</h3>
        <Button size="sm" data-testid="create-waiver-btn" onClick={() => setShowCreate(true)}>
          <PlusCircle className="w-4 h-4 mr-1" /> New Request
        </Button>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="waiver-requests-table">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Ref</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Req.</th>
                <th className="py-2 pr-3">Appr.</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground text-xs">No waiver requests yet</td></tr>
              )}
              {requests.map(wr => (
                <tr key={wr.id} className="border-b hover:bg-muted/30" data-testid={`waiver-row-${wr.id}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{wr.request_ref}</td>
                  <td className="py-2 pr-3">{wr.waiver_type_name}</td>
                  <td className="py-2 pr-3">{wr.requested_amount}</td>
                  <td className="py-2 pr-3">{wr.approved_amount || "—"}</td>
                  <td className="py-2 pr-3"><StatusBadge status={wr.status} /></td>
                  <td className="py-2 flex gap-1 flex-wrap">
                    {["REQUESTED","UNDER_REVIEW"].includes(wr.status) && (<>
                      <Button size="xs" variant="outline" data-testid={`approve-waiver-${wr.id}`} onClick={() => approve(wr.id)}>Approve</Button>
                      <Button size="xs" variant="outline" data-testid={`reject-waiver-${wr.id}`} onClick={() => reject(wr.id)}>Reject</Button>
                    </>)}
                    {wr.status === "APPROVED" && (
                      <Button size="xs" variant="outline" data-testid={`apply-waiver-${wr.id}`} onClick={() => apply(wr.id)}>Apply</Button>
                    )}
                    {wr.status === "APPLIED" && (
                      <Button size="xs" variant="outline" data-testid={`reverse-waiver-${wr.id}`} onClick={() => setReverseTarget(wr)}>
                        <RotateCcw className="w-3 h-3 mr-1" />Reverse
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CreateWaiverModal open={showCreate} onClose={() => setShowCreate(false)} token={token} waiverTypes={waiverTypes} onCreated={load} />
      {reverseTarget && <ReverseModal open={!!reverseTarget} onClose={() => setReverseTarget(null)} token={token} entity={reverseTarget} entityType="waiver" onDone={load} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function ScholarshipsWaivers() {
  const [tab, setTab] = useState("awards");
  const [schemes, setSchemes] = useState([]);
  const [waiverTypes, setWaiverTypes] = useState([]);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, wRes] = await Promise.all([
          axios.get(`${API}/api/finance/fm4a/schemes`, { headers: hdrs(token) }),
          axios.get(`${API}/api/finance/fm4a/waiver-types`, { headers: hdrs(token) }),
        ]);
        setSchemes(sRes.data);
        setWaiverTypes(wRes.data);
      } catch { /* handled per-tab */ }
    };
    load();
  }, [token]);

  const tabs = [
    { id: "awards", label: "Scholarship Awards" },
    { id: "waivers", label: "Waiver Requests" },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="scholarships-waivers-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Scholarships & Waivers</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage scholarship awards and fee waiver requests. Reductions are posted via credit memos — invoices are not mutated.</p>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 border-b mb-6">
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-card border rounded-lg p-5">
        {tab === "awards" && <AwardsTab token={token} schemes={schemes} />}
        {tab === "waivers" && <WaiverRequestsTab token={token} waiverTypes={waiverTypes} />}
      </div>
    </div>
  );
}
